import flatpickr from 'flatpickr'
import locales from 'flatpickr/dist/l10n'
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js'

// Wire formats (what the server reads/writes) per picker mode. The wire format
// stays Carbon::parse()-compatible so the PHP field classes need no changes.
// Ranges travel as a single 'Y-m-d|Y-m-d' composite ('Y-m-d|' = open end).
// Months travel as 'Y-m' (model-filter's month_format), the whole month.
//
// The DISPLAY format comes from the field's ->altFormat() / the blade `format`
// prop (PHP tokens, converted below). Only when one is set an altInput is
// used; without it the input shows the wire format directly.
const MODES = {
    date: {
        dateFormat: 'Y-m-d',
        enableTime: false,
        noCalendar: false,
        displayLength: 10,
    },
    month: {
        dateFormat: 'Y-m',
        enableTime: false,
        noCalendar: false,
        displayLength: 7,
        // flatpickr's monthSelect plugin replaces the day grid with a month grid.
        // It binds its year navigation in onReady, which the mobile setup never
        // builds, so this mode keeps the calendar on every device — like ranges.
        isMonth: true,
    },
    datetime: {
        dateFormat: 'Y-m-d H:i',
        enableTime: true,
        noCalendar: false,
        displayLength: 16,
    },
    time: {
        dateFormat: 'H:i',
        enableTime: true,
        noCalendar: true,
        displayLength: 5,
    },
    range: {
        dateFormat: 'Y-m-d',
        enableTime: false,
        noCalendar: false,
        isRange: true,
    },
}

// Convert a PHP date format to flatpickr tokens. Most date tokens are identical,
// while seconds, am/pm, and 12-hour tokens differ. Flatpickr has no unpadded
// 24-hour token, so PHP `G` uses its padded `H` as the closest representation.
// PHP's `jS` ordinal day is one flatpickr `J` token; a standalone PHP `S` has no
// flatpickr equivalent. Escaped characters (\\T) pass through.
function convertPhpFormat(format) {
    if (!format) return null

    let converted = ''

    for (let i = 0; i < format.length; i++) {
        const char = format[i]

        if (char === '\\') {
            converted += char + (format[++i] ?? '')
            continue
        }

        if (char === 'j' && format[i + 1] === 'S') {
            converted += 'J'
            i++
            continue
        }

        if (char === 'S') {
            throw new Error(
                'Unsupported PHP date format token "S": only "jS" ordinal days are supported',
            )
        }

        converted += {
            s: 'S',
            a: 'K',
            A: 'K',
            g: 'h',
            h: 'G',
            G: 'H',
        }[char] ?? char
    }

    return converted
}

// flatpickr's `Y` parser accepts exactly four digits. When allowInput is enabled,
// entering `01.01.23` into a `d.m.Y` field therefore parses only day/month and
// silently leaves the parser's initial year (the current one) in place. Normalize a
// complete two- or three-digit year before handing the value to flatpickr:
//
//   23   -> 2023 (the same century rule as flatpickr's `y` token)
//   100  -> 0100
//   0020 -> 0020 (four authored digits are always literal)
//
// One digit is deliberately not guessed. These are purpose-built locator patterns, not
// a literal copy of flatpickr's `tokenRegex`: its initially empty D/F/K/M/l patterns are
// filled by setupLocale(). This base table has generic fallbacks; once the picker exists,
// createInputTokenPatterns() replaces them with that instance's escaped locale alternatives.
// U/u are narrowed from flatpickr's `.+` to their numeric semantics so they cannot consume
// a following year. Real-instance integration tests guard these deliberate differences.
const INPUT_TOKEN_PATTERNS = {
    D: '\\D+',
    F: '\\D+',
    G: '\\d{1,2}',
    H: '\\d{1,2}',
    J: '\\d{1,2}\\D+',
    K: '\\D+',
    M: '\\D+',
    S: '\\d{1,2}',
    U: '-?\\d+',
    W: '\\d{1,2}',
    Z: '.+',
    d: '\\d{1,2}',
    h: '\\d{1,2}',
    i: '\\d{1,2}',
    j: '\\d{1,2}',
    l: '\\D+',
    m: '\\d{1,2}',
    n: '\\d{1,2}',
    s: '\\d{1,2}',
    u: '-?\\d+',
    w: '\\d{1,2}',
    y: '\\d{2}',
}

const INPUT_FORMAT_TOKENS = new Set([...Object.keys(INPUT_TOKEN_PATTERNS), 'Y'])
const DIGIT_CONSUMING_INPUT_TOKENS = new Set([
    'G', 'H', 'J', 'S', 'U', 'W', 'Y', 'Z',
    'd', 'h', 'i', 'j', 'm', 'n', 's', 'u', 'w', 'y',
])
const TWELVE_HOUR_INPUT_TOKENS = new Set(['G', 'h', 'K'])
const LOCALE_INPUT_TOKENS = new Set(['D', 'F', 'K', 'M', 'l'])

function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Viewports narrower than the month grid itself get the locale's abbreviated
// month names; everything from a small phone up shows the full names.
const SHORTHAND_MONTHS_BELOW_WIDTH = 360

function usesShorthandMonths() {
    return typeof window !== 'undefined'
        && typeof window.innerWidth === 'number'
        && window.innerWidth < SHORTHAND_MONTHS_BELOW_WIDTH
}

// The first element matching any of `selectors`, tried in the given order —
// unlike a single selector list, which returns document order.
function firstMatch(container, selectors) {
    for (const selector of selectors) {
        const match = container?.querySelector?.(selector)
        if (match) return match
    }

    return null
}

function localeAlternative(values, fallback) {
    const alternatives = [...new Set(values.filter(Boolean))]

    return alternatives.length > 0
        ? `(?:${alternatives.map((value) => escapeRegExp(value)).join('|')})`
        : fallback
}

function createInputTokenPatterns(l10n = {}) {
    const amPM = l10n.amPM ?? []

    return {
        ...INPUT_TOKEN_PATTERNS,
        D: localeAlternative(l10n.weekdays?.shorthand ?? [], INPUT_TOKEN_PATTERNS.D),
        F: localeAlternative(l10n.months?.longhand ?? [], INPUT_TOKEN_PATTERNS.F),
        K: localeAlternative([...amPM, ...amPM.map((value) => value.toLowerCase())], INPUT_TOKEN_PATTERNS.K),
        M: localeAlternative(l10n.months?.shorthand ?? [], INPUT_TOKEN_PATTERNS.M),
        l: localeAlternative(l10n.weekdays?.longhand ?? [], INPUT_TOKEN_PATTERNS.l),
    }
}

function inputLiteralPattern(literal) {
    // flatpickr uses `.` for every literal and therefore accepts alternate visible
    // separators. A non-digit match keeps that tolerance without allowing the separator
    // immediately before Y to consume one of the year digits.
    return /\d/u.test(literal) ? literal : '\\D'
}

function formatUnitPatterns(format, tokenPatterns = INPUT_TOKEN_PATTERNS) {
    const patterns = []

    for (let i = 0; i < format.length; i++) {
        const token = format[i]

        if (token === '\\') {
            if (++i < format.length) patterns.push(inputLiteralPattern(format[i]))
            continue
        }

        patterns.push(tokenPatterns[token] ?? inputLiteralPattern(token))
    }

    return patterns
}

function formatPartPattern(format, tokenPatterns) {
    return formatUnitPatterns(format, tokenPatterns).join('')
}

function assertSupportedInputFormat(format) {
    let previousToken = null
    let yearCount = 0

    for (let i = 0; i < format.length; i++) {
        const token = format[i]

        if (token === '\\') {
            // An escaped literal is a visible boundary between input tokens.
            i++
            previousToken = null
            continue
        }

        const isToken = INPUT_FORMAT_TOKENS.has(token)

        if (
            isToken
            && previousToken !== null
            && DIGIT_CONSUMING_INPUT_TOKENS.has(previousToken)
            && DIGIT_CONSUMING_INPUT_TOKENS.has(token)
        ) {
            throw new Error(
                `Unsupported flatpickr input format "${format}": separate adjacent numeric date/time tokens`,
            )
        }

        if (token === 'Y') yearCount++

        previousToken = isToken ? token : null
    }

    if (yearCount > 1) {
        throw new Error(
            `Unsupported flatpickr input format "${format}": use at most one unescaped Y token`,
        )
    }
}

function containsUnescapedInputToken(format, tokens) {
    for (let i = 0; i < format.length; i++) {
        if (format[i] === '\\') {
            i++
            continue
        }

        if (tokens.has(format[i])) return true
    }

    return false
}

function closestFocusTrap(element) {
    let current = element?.parentElement ?? null

    while (current) {
        const trapsFocus = current.hasAttribute('data-resourcerer-focus-scope')
            || [...current.attributes].some(
                ({ name }) => name === 'x-trap' || name.startsWith('x-trap.'),
            )

        if (trapsFocus) return current

        current = current.parentElement
    }

    return null
}

function positionInsideFocusTrap(focusTrap) {
    return (instance, customPositionElement) => {
        const calendar = instance.calendarContainer
        const positionElement = customPositionElement
            ?? instance._positionElement
            ?? instance.altInput
            ?? instance.input
            ?? instance.element

        if (!calendar || !positionElement) return

        const inputBounds = positionElement.getBoundingClientRect()
        const offsetParent = calendar.offsetParent ?? focusTrap
        const offsetParentBounds = offsetParent.getBoundingClientRect()
        const calendarHeight = [...calendar.children]
            .reduce((height, child) => height + child.offsetHeight, 0)
            || calendar.offsetHeight
        const calendarWidth = calendar.offsetWidth
        // Resourcerer keeps flatpickr's default auto/left behavior while
        // translating viewport coordinates into the dialog's containing block.
        const showOnTop = window.innerHeight - inputBounds.bottom < calendarHeight
            && inputBounds.top > calendarHeight

        calendar.classList.toggle('arrowTop', !showOnTop)
        calendar.classList.toggle('arrowBottom', showOnTop)

        let targetTop = showOnTop
            ? inputBounds.top - calendarHeight - 2
            : inputBounds.bottom + 2
        let targetLeft = inputBounds.left

        const viewportMargin = 8
        const maximumTop = Math.max(
            viewportMargin,
            window.innerHeight - calendarHeight - viewportMargin,
        )
        targetTop = Math.min(maximumTop, Math.max(viewportMargin, targetTop))
        const maximumLeft = Math.max(
            viewportMargin,
            window.innerWidth - calendarWidth - viewportMargin,
        )
        targetLeft = Math.min(maximumLeft, Math.max(viewportMargin, targetLeft))

        calendar.style.top = `${targetTop - offsetParentBounds.top + offsetParent.scrollTop}px`
        calendar.style.left = `${targetLeft - offsetParentBounds.left + offsetParent.scrollLeft}px`
        calendar.style.right = 'auto'
    }
}

function unescapedYearIndex(format) {
    let index = -1

    for (let i = 0; i < format.length; i++) {
        if (format[i] === '\\') {
            i++
            continue
        }

        if (format[i] !== 'Y') continue

        // Multiple Y tokens are rejected by assertSupportedInputFormat(). Keeping this
        // fallback makes "no Y" and an invalid standalone call equally non-destructive.
        if (index !== -1) return -1

        index = i
    }

    return index
}

function normalizeInputYear(value, format, tokenPatterns) {
    if (typeof value !== 'string' || !format) return value

    const yearIndex = unescapedYearIndex(format)
    if (yearIndex === -1) return value

    const trimmed = value.trim()
    if (trimmed === '') return value

    const prefixFormat = format.slice(0, yearIndex)
    const prefix = formatPartPattern(prefixFormat, tokenPatterns)
    const match = new RegExp(`^(${prefix})(\\d*)(.*)$`, 'u').exec(trimmed)

    // A prefix mismatch means the parser cannot reach Y and would retain its current-year
    // base date. Missing/partial suffixes after a complete year remain valid (for example
    // a Datetime entered without its time), because the suffix is deliberately not matched.
    if (!match) return null

    // A format containing `Y` must not fall back to flatpickr's partial parser when
    // fewer than two digits were authored, and must not let its unanchored four-digit
    // token silently truncate a longer typo. Exactly two to four digits are supported.
    if (match[2].length < 2 || match[2].length > 4) return null

    const year = match[2].length === 2
        ? `20${match[2]}`
        : match[2].padStart(4, '0')

    return `${match[1]}${year}${match[3]}`
}

// Resolve a flatpickr locale object from a code like 'de', 'de-DE' or Laravel's
// 'de_DE'. flatpickr keys region locales with an underscore ('zh_tw'), so both
// notations normalize to that; otherwise the base language is used.
// Unknown codes (incl. 'en') fall back to flatpickr's built-in default.
function resolveLocale(code) {
    if (!code) return 'default'

    const normalized = String(code).toLowerCase().replace('-', '_')

    return locales[normalized] ?? locales[normalized.split('_')[0]] ?? 'default'
}

// aria-controls needs a target id and flatpickr gives its calendar none. Count
// instances instead of generating one, so the same page never collides.
let calendarInstances = 0

export default (entangled, config = {}) => {
    // The flatpickr instance is kept in the factory closure on purpose:
    // storing it in Alpine state would wrap it in a reactive proxy and
    // deep-observe flatpickr internals.
    let picker = null
    let restoreAuthoredAttributes = null
    let removeCapturedEventListeners = null
    let removeDirectInputListeners = null
    let removeCalendarEscapeListener = null
    // Installed on desktop only — see the combobox block below. Mobile hands the
    // field to a native input, which carries its own semantics.
    let setAriaExpanded = null

    const mode = config.mode ?? 'date'
    const spec = MODES[mode] ?? MODES.date
    const isRange = spec.isRange ?? false
    const isMonth = spec.isMonth ?? false
    const locale = resolveLocale(config.locale ?? document.documentElement.lang)
    // One-day ranges: by default the composite carries the day twice ('d|d');
    // with nullifyEqualEnd the end stays empty ('d|') — for apps whose
    // convention is "single day = no end" (validation then uses 'after').
    const nullifyEqualEnd = config.nullifyEqualEnd ?? false

    const formatWire = (dates) => {
        const formatted = dates.map((date) => picker.formatDate(date, spec.dateFormat))

        if (!isRange) return formatted[0] ?? ''

        const parts = formatted.slice(0, 2)

        if (parts.length === 1) {
            parts.push(nullifyEqualEnd ? '' : parts[0])
        } else if (nullifyEqualEnd && parts[0] === parts[1]) {
            parts[1] = ''
        }

        return parts.join('|')
    }

    const parseWire = (value) => {
        if (isRange) {
            return String(value ?? '')
                .split('|')
                .map((part) => parseServerValue(part, mode))
                .filter((date) => date !== null)
        }

        return parseServerValue(value, mode)
    }

    return {
        value: entangled,

        // Value for readonly/disabled rendering (no picker there).
        get display() {
            const value = typeof this.value === 'string' ? this.value : ''

            if (isRange) {
                return value.replace('|', ' – ').replace(/\s–\s$/, '')
            }

            return value.replace('T', ' ').substring(0, spec.displayLength)
        },

        init() {
            const input = this.$refs.input
            if (!input || input.readOnly || input.disabled) return

            // Conversion can reject unsupported picker-only PHP tokens. Keep it behind
            // the guard: readonly/disabled fields never construct a picker and therefore
            // do not need a parseable direct-input format.
            const altFormat = convertPhpFormat(config.displayFormat)
            const inputFormat = altFormat ?? spec.dateFormat

            // Adjacent numeric input tokens are ambiguous (`Ymd` + `260422`, for
            // example). Text tokens provide their own boundary (`h:iK`), while compact
            // numeric formats must use a separated altFormat for the editable picker.
            assertSupportedInputFormat(inputFormat)

            const minDate = parseServerValue(input.getAttribute('min'), mode)
            const maxDate = parseServerValue(input.getAttribute('max'), mode)
            const focusTrap = closestFocusTrap(input)
            const focusTrapPortal = focusTrap?.querySelector(
                '[data-resourcerer-flatpickr-portal]',
            ) ?? focusTrap

            // The raw value of the last pick made on the NATIVE mobile input, captured
            // before flatpickr consumes it. The library drops a value outside min/max and
            // blanks the input before firing onChange, so by then a rejected pick is
            // indistinguishable from a deliberate clear. This is what tells them apart.
            //
            // Deliberately mobile-only. Typing into the desktop calendar field
            // (allowInput) can produce the same ambiguity, but that predates the native
            // pickers and reconstructing intent there needs a different design than
            // guessing after the fact — see the known issue in the 0.35.2 changelog.
            let pendingEntry = null

            // Set only while a REJECTED pick is being undone. flatpickr dispatches change
            // and input on the source input after every onChange, and for a pick it threw
            // away that pair describes nothing at all.
            //
            // Deliberately not extended to a commit that merely stores nothing: a picker
            // confirmed on its existing value is not a value change, but it IS an intent —
            // the auto-tick in form/input.blade.php turns it into "yes, activate this
            // field". Swallowing it made a deliberate Done save null on mobile while the
            // same gesture worked on desktop.
            let undoingRejectedPick = false

            // Closing because an ancestor scrolled is a layout reaction, not input
            // confirmation. flatpickr fires onClose synchronously from close(), so this
            // flag can keep that path from committing a half range or direct-input draft.
            let closingOnScroll = false

            // Downstream listeners (the nullable-checkbox auto-tick, consumer handlers)
            // are bound to 'input', which a native input fires on user edits.
            //
            // dispatchInput: false for commits made from onChange — flatpickr dispatches
            // change AND input on this very element itself once the onChange hooks
            // return (triggerEvent), so firing our own would run every listener twice.
            // No other path gets an event from the library.
            // The stored value re-expressed at wire granularity, so comparing ignores
            // precision the wire format cannot carry. Single values only — see commit().
            const asWire = (value) => {
                const parsed = parseServerValue(value, mode)

                return parsed ? picker.formatDate(parsed, spec.dateFormat) : ''
            }

            const commit = (dates, { dispatchInput = true } = {}) => {
                const wire = formatWire(dates)

                // Single values compare at wire granularity, not as raw strings: the
                // server may hand over more precision than the wire format writes back
                // (`Datetime` form state is 'Y-m-d\TH:i:s'), and a native picker fires
                // change when it is merely confirmed. On raw strings that no-op looked
                // like a change — it rewrote the value, dropped the seconds and marked
                // the form dirty without the user editing anything.
                //
                // Ranges compare RAW. Their wire format is 'Y-m-d', so there is no
                // sub-wire precision to absorb, and normalising only blurs composite
                // shapes that mean different things: an open end against a closed one-day
                // range, and an unparseable end against an absent one — which stopped the
                // very selection that would have repaired a broken record.
                const stored = isRange ? (this.value ?? '') : asWire(this.value)

                if (stored === wire) return

                this.value = wire

                if (dispatchInput) {
                    input.dispatchEvent(new Event('input', { bubbles: true }))
                }
            }

            picker = flatpickr(input, {
                // Hand over to the OS picker on mobile. The calendar is unusable there:
                // allowInput keeps the input editable, so tapping the field opened the
                // calendar AND the text keyboard on top of each other. flatpickr swaps
                // in a native input of the matching type and hides its own.
                // Single-value day/time modes only — ranges have no native input and
                // keep the calendar (flatpickr requires mode === 'single' for the
                // swap), and the month grid plugin cannot initialize against the
                // mobile setup (see MODES.month), so months keep the calendar too.
                disableMobile: isMonth,
                // The plugin overwrites dateFormat/altFormat with its own config, so it
                // receives exactly the formats configured below.
                // Full month names from the locale; the abbreviated form is used
                // only where the grid's fixed width would not fit on the viewport.
                ...isMonth ? {
                    plugins: [monthSelectPlugin({
                        shorthand: usesShorthandMonths(),
                        dateFormat: spec.dateFormat,
                        altFormat: altFormat ?? spec.dateFormat,
                        theme: 'light',
                    })],
                } : {},
                allowInput: true,
                // Focus alone (dialog autofocus, Tab, programmatic focus) must leave
                // the picker closed so the input remains ready for direct entry. A
                // deliberate click is wired to open it below.
                clickOpens: false,
                // 12h clock only when the display format asks for an unescaped G/h/K.
                // Keep this option explicit: reactivating an instance locale before a
                // localized parse must not let setupLocale() replace the chosen clock.
                time_24hr: !containsUnescapedInputToken(inputFormat, TWELVE_HOUR_INPUT_TOKENS),
                enableTime: spec.enableTime,
                noCalendar: spec.noCalendar,
                mode: isRange ? 'range' : 'single',
                dateFormat: spec.dateFormat,
                // An altInput only exists when a display format is configured;
                // without one the input shows the wire format directly.
                altInput: altFormat !== null,
                ...altFormat === null ? {} : { altFormat },
                locale,
                minDate: minDate ?? undefined,
                maxDate: maxDate ?? undefined,
                defaultDate: parseWire(this.value),
                ...focusTrap === null ? {} : {
                    // Alpine's focus trap only accepts descendants. Flatpickr normally
                    // portals to <body>, so its hour/minute inputs immediately lose
                    // focus in an ActionDialog. Keep the popup inside the trap and
                    // translate viewport coordinates into that containing block.
                    appendTo: focusTrapPortal,
                    position: positionInsideFocusTrap(focusTrap),
                },
                onChange: (selectedDates) => {
                    // Ranges commit only complete selections — the first click of
                    // a fresh range must not push a half state to the server.
                    if (isRange && selectedDates.length < 2) return

                    const entry = pendingEntry
                    pendingEntry = null

                    // An empty selection straight after a non-empty entry means flatpickr
                    // rejected it as out of min/max range. Committing the empty result
                    // would silently wipe the stored value, so put it back — the calendar
                    // refuses a disabled day the same way.
                    if (selectedDates.length === 0 && entry) {
                        undoingRejectedPick = true
                        picker.setDate(parseWire(this.value), false)

                        return
                    }

                    commit(selectedDates, { dispatchInput: false })

                    // flatpickr may have moved the native input to a value below wire
                    // granularity (seconds, with step="1"). Put the display back on what
                    // was actually stored — whether the pick changed the wire value or
                    // not, since either way the remainder would stay on screen.
                    // Idempotent for an ordinary pick: setDate re-asserts the same instant
                    // and, with triggerChange off, fires no hooks.
                    if (picker.mobileInput) {
                        picker.setDate(parseWire(this.value), false)
                    }
                },
                onOpen: () => setAriaExpanded?.(true),
                onClose: (selectedDates) => {
                    // Before the scroll guard: the calendar did close either way.
                    setAriaExpanded?.(false)

                    if (closingOnScroll) return

                    // A single picked day closes as a one-day range.
                    if (isRange && selectedDates.length === 1) {
                        commit([selectedDates[0], selectedDates[0]])

                        return
                    }

                    // flatpickr parses allowInput text on click-away with
                    // triggerChange=false. No onChange hook follows, so onClose is the
                    // only place that can carry that direct entry into Livewire.
                    //
                    // A calendar click is ordered differently: flatpickr closes first
                    // and fires onChange afterwards. Defer this fallback by one microtask
                    // so that normal path wins and dispatches exactly one input event.
                    const dates = [...selectedDates]
                    queueMicrotask(() => {
                        if (picker) commit(dates)
                    })
                },
            })

            restoreAuthoredAttributes = adoptNativeInput(picker.mobileInput, input)
            applyNativeBounds(picker.mobileInput, minDate, maxDate)

            if (picker.mobileInput) {
                const scope = input.parentNode

                // Capture on the shared parent covers both the visible native input and
                // flatpickr's hidden source. It sees native changes before flatpickr's
                // target/bubble listener and can stop the library's synthetic source
                // events before they reach either their target or page-level consumers.
                const onCapturedEvent = (event) => {
                    if (undoingRejectedPick && event.target === input) {
                        event.stopImmediatePropagation()

                        // The pair is dispatched change first, input second.
                        if (event.type === 'input') undoingRejectedPick = false

                        return
                    }

                    // Only a real change on the native input, never flatpickr's own
                    // dispatch on the source: recording that would leave a stale entry
                    // behind and make the next legitimate empty onChange — clear(true),
                    // setDate('', true) — look like a rejected pick.
                    if (event.type === 'change' && event.target === picker.mobileInput) {
                        pendingEntry = event.target.value
                    }
                }

                for (const type of ['change', 'input']) {
                    scope?.addEventListener(type, onCapturedEvent, true)
                }

                removeCapturedEventListeners = () => {
                    for (const type of ['change', 'input']) {
                        scope?.removeEventListener(type, onCapturedEvent, true)
                    }
                }
            } else {
                const visibleInput = picker.altInput ?? input

                // Focus no longer opens the calendar, which leaves Alt+ArrowDown as
                // the only keyboard opener — undiscoverable unless the field says it
                // owns a popup. These are the APG date-picker combobox semantics, and
                // they also let a screen reader announce open/closed. Whatever the
                // consumer authored is recorded and put back on teardown, because
                // without an altInput this is their own element.
                const calendarContainer = picker.calendarContainer

                if (calendarContainer && !calendarContainer.id) {
                    calendarContainer.id = `resourcerer-flatpickr-calendar-${++calendarInstances}`
                }

                const authoredAria = {
                    'role': visibleInput.getAttribute('role'),
                    'aria-haspopup': visibleInput.getAttribute('aria-haspopup'),
                    'aria-expanded': visibleInput.getAttribute('aria-expanded'),
                    'aria-controls': visibleInput.getAttribute('aria-controls'),
                }

                setAriaExpanded = (expanded) => visibleInput.setAttribute(
                    'aria-expanded',
                    expanded ? 'true' : 'false',
                )

                visibleInput.setAttribute('role', 'combobox')
                visibleInput.setAttribute('aria-haspopup', 'dialog')
                setAriaExpanded(picker.isOpen)

                if (calendarContainer) {
                    // aria-haspopup names a role the popup has to carry, and a
                    // dialog needs an accessible name. flatpickr gives its calendar
                    // neither. The name comes from the field label the blade passes
                    // through, so it is already localized; the fallback only applies
                    // to a hand-rolled usage that passes nothing.
                    calendarContainer.setAttribute('role', 'dialog')
                    calendarContainer.setAttribute(
                        'aria-label',
                        String(config.calendarLabel ?? '').trim() || 'Calendar',
                    )

                    visibleInput.setAttribute('aria-controls', calendarContainer.id)
                }
                if (calendarContainer) {
                    const onCalendarKeydown = (event) => {
                        if (event.key !== 'Escape' || !picker?.isOpen) return

                        event.preventDefault()
                        event.stopPropagation()
                        picker.close()
                        visibleInput.focus()
                    }

                    calendarContainer.addEventListener('keydown', onCalendarKeydown, true)
                    removeCalendarEscapeListener = () => {
                        calendarContainer.removeEventListener('keydown', onCalendarKeydown, true)
                    }
                }

                // Opening from the keyboard must land in the grid. With allowInput
                // flatpickr deliberately keeps its key handling off while the caret
                // is in the text input (onKeyDown: `allowKeydown = isOpen && (!allowInput
                // || !isInput)`), so a calendar opened without moving focus is visible
                // but not operable — the arrow keys still drive the caret. Once a day
                // has focus, flatpickr's own navigation and its Escape handling take
                // over, and Escape returns focus to the input.
                //
                // A click deliberately does NOT move focus: the pointer user is
                // clicking to place the caret or to pick a day directly, and pulling
                // focus out of the text input would break typing.
                //
                // The month grid (monthSelect plugin) builds its cells without the
                // `.flatpickr-day` class and marks the current month `.today` itself;
                // flatpickr's own todayDateElem still points at the detached day cell
                // it built before the plugin replaced the grid, so only a connected
                // element counts. The fallbacks are tried in order: current month,
                // first enabled day, first enabled month.
                const focusCalendar = () => {
                    const target = [picker.selectedDateElem, picker.todayDateElem]
                        .find(element => element?.isConnected)
                        ?? firstMatch(calendarContainer, [
                            '.flatpickr-monthSelect-month.today:not(.flatpickr-disabled)',
                            '.flatpickr-day:not(.flatpickr-disabled)',
                            '.flatpickr-monthSelect-month:not(.flatpickr-disabled)',
                        ])
                        ?? picker.hourElement

                    target?.focus()
                }

                const usesLocaleParser = containsUnescapedInputToken(
                    inputFormat,
                    LOCALE_INPUT_TOKENS,
                )

                // Keep the four-digit display format while accepting a complete short
                // year. A target capture listener runs before flatpickr's target/bubble
                // blur and Enter listeners, so the library keeps handling all remaining
                // locale-specific tokens itself. setupLocale() injects concrete
                // month/day/am-pm alternatives into the instance parser; a parseDate
                // override would replace that parser wholesale.
                const normalizeDirectInput = () => {
                    if (usesLocaleParser) {
                        // flatpickr 4.6.13 stores its locale tokenRegex at module scope, so
                        // the most recently initialized picker otherwise wins for every
                        // instance. Re-running this instance's public locale setter rebuilds
                        // its parser and makes its alternatives current. set() also refreshes
                        // the displayed value; preserve the text the user actually authored.
                        const authoredValue = visibleInput.value
                        picker.set('locale', picker.config.locale)
                        visibleInput.value = authoredValue
                    }

                    const tokenPatterns = createInputTokenPatterns(picker.l10n)
                    const separator = isRange ? picker.l10n.rangeSeparator : null
                    const parts = separator === null
                        ? [visibleInput.value]
                        : visibleInput.value.split(separator)
                    const normalized = parts.map((part) => normalizeInputYear(
                        part,
                        inputFormat,
                        tokenPatterns,
                    ))

                    // Do not let flatpickr partially parse an incomplete `Y`: that is
                    // what silently substitutes the current year. Its ordinary invalid-
                    // input path clears the field instead.
                    visibleInput.value = normalized.includes(null)
                        ? ''
                        : normalized.join(separator ?? '')
                }

                const onDirectInputKeydown = (event) => {
                    if (event.altKey && event.key === 'ArrowDown') {
                        event.preventDefault()
                        // flatpickr's own listener on this input would still hand the
                        // ArrowDown to plugin hooks — the month grid's navigation moves
                        // the focus just placed below by a whole row.
                        event.stopImmediatePropagation()
                        picker.open()
                        focusCalendar()

                        return
                    }

                    // flatpickr's own Escape handler is unreachable while the caret is
                    // in the text input: with allowInput it only runs for events coming
                    // from the calendar itself. That did not matter while focus opened
                    // the calendar; now that Alt+ArrowDown is the keyboard opener, the
                    // way back has to exist too. Closing commits whatever was typed,
                    // exactly as clicking away does. The event is stopped only when
                    // something was actually closed, so an Escape on a closed field
                    // still reaches whatever surrounds it — a dialog, most likely.
                    if (event.key === 'Escape' && picker.isOpen) {
                        event.preventDefault()
                        event.stopPropagation()
                        picker.close()

                        return
                    }

                    if (event.key === 'Enter') normalizeDirectInput()
                }

                const onDirectInputClick = () => picker.open()

                const onDocumentPointerDown = (event) => {
                    if (!picker?.isOpen || event.target === visibleInput) return
                    if (picker.calendarContainer?.contains(event.target)) return

                    normalizeDirectInput()
                }

                const onDocumentScroll = (event) => {
                    if (!picker?.isOpen || event.target === visibleInput) return

                    // A calendar appended to <body> keeps its viewport coordinates when
                    // an intermediate container scrolls, while the input moves away.
                    // Close only when the document or an input ancestor moved; scrolling
                    // an unrelated pane (or calendar content) cannot detach this picker.
                    const target = event.target
                    const movedInput = target === document
                        || (target instanceof Element && target.contains(visibleInput))

                    if (movedInput) {
                        closingOnScroll = true

                        try {
                            picker.close()
                        } finally {
                            closingOnScroll = false
                        }
                    }
                }

                // flatpickr only fires onChange for parseable input — clearing the
                // visible field by hand must still reach the server value.
                //
                // Mobile is deliberately excluded: the native input fires its own change
                // event when cleared and flatpickr turns that into an empty onChange, so
                // this would be redundant there — and harmful, because a stored value the
                // picker cannot parse leaves the native input empty, which would let a
                // bare tap-in/tap-out wipe it.
                const clearDirectInput = () => {
                    if (!picker || visibleInput.value.trim() !== '') return
                    if ((this.value ?? '') === '') return

                    picker.clear(false)
                    this.value = ''
                    input.dispatchEvent(new Event('input', { bubbles: true }))
                }

                visibleInput.addEventListener('keydown', onDirectInputKeydown, true)
                visibleInput.addEventListener('click', onDirectInputClick)
                visibleInput.addEventListener('blur', normalizeDirectInput, true)
                visibleInput.addEventListener('blur', clearDirectInput)
                document.addEventListener('mousedown', onDocumentPointerDown, true)
                document.addEventListener('touchstart', onDocumentPointerDown, true)
                document.addEventListener('scroll', onDocumentScroll, true)

                removeDirectInputListeners = () => {
                    setAriaExpanded = null

                    // The calendar container is flatpickr's own element and is gone
                    // by now on destroy(); only the input has to be handed back.

                    for (const [name, authored] of Object.entries(authoredAria)) {
                        if (authored === null) {
                            visibleInput.removeAttribute(name)
                        } else {
                            visibleInput.setAttribute(name, authored)
                        }
                    }

                    visibleInput.removeEventListener('keydown', onDirectInputKeydown, true)
                    visibleInput.removeEventListener('click', onDirectInputClick)
                    visibleInput.removeEventListener('blur', normalizeDirectInput, true)
                    visibleInput.removeEventListener('blur', clearDirectInput)
                    document.removeEventListener('mousedown', onDocumentPointerDown, true)
                    document.removeEventListener('touchstart', onDocumentPointerDown, true)
                    document.removeEventListener('scroll', onDocumentScroll, true)
                }
            }

            this.$watch('value', (newValue) => {
                if (!picker) return

                const current = picker.selectedDates.length
                    ? formatWire(picker.selectedDates)
                    : ''

                if ((newValue ?? '') === current) return

                if (!newValue) {
                    picker.clear(false)

                    return
                }

                picker.setDate(parseWire(newValue), false)
            })
        },

        destroy() {
            // destroy() removes the native input, so whatever was moved onto it goes
            // back to the source — the component must leave the DOM as it found it.
            removeCalendarEscapeListener?.()
            removeCalendarEscapeListener = null

            picker?.destroy()
            picker = null

            removeCapturedEventListeners?.()
            removeCapturedEventListeners = null

            removeDirectInputListeners?.()
            removeDirectInputListeners = null

            restoreAuthoredAttributes?.()
            restoreAuthoredAttributes = null
        },
    }
}

// Attributes the native input already owns: flatpickr sets them itself (type, class,
// value, step, disabled, required, placeholder) or this module manages them (tabindex,
// and the bounds via applyNativeBounds).
const NATIVE_OWNED_ATTRIBUTES = new Set([
    'type', 'class', 'value', 'step',
    'disabled', 'required', 'placeholder',
    'tabindex', 'min', 'max',
    // Deliberately left behind: the native input holds flatpickr's own mobile format
    // ('Y-m-d\\TH:i:S'), so a plain form submit has to post the source input's wire value.
    // 'form' goes with it — name and form association are a pair, and splitting them
    // across two elements submits neither.
    'name', 'form',
])

// Alpine and Livewire directives must not be re-declared on a second element: Alpine
// picks up inserted nodes through its mutation observer and would bind them twice.
const FRAMEWORK_DIRECTIVE = /^(x-|wire:|@|:|v-)/

// flatpickr builds the native mobile input itself and copies only className, disabled,
// required, placeholder and step off the source. Everything else the field authored —
// id, name, aria-*, data-*, autocomplete — stays behind on an element flatpickr has just
// switched to type="hidden", where it no longer applies to anything the user can see.
// It also hard-codes tabIndex = 1, and a positive tabindex precedes every tabindex="0"
// control on the page.
//
// Returns the undo for destroy(), or null off mobile where there is no native input.
function adoptNativeInput(mobileInput, input) {
    if (!mobileInput) return null

    // Moved rather than copied: the source input is hidden from here on, so a duplicate
    // id would be invalid and a duplicate data-* hook would only make selectors ambiguous.
    const moved = []

    for (const { name, value } of [...input.attributes]) {
        if (NATIVE_OWNED_ATTRIBUTES.has(name) || FRAMEWORK_DIRECTIVE.test(name)) continue

        input.removeAttribute(name)
        mobileInput.setAttribute(name, value)
        moved.push([name, value])
    }

    // Read the attribute, not input.tabIndex: flatpickr has already switched the source
    // to type="hidden", whose reflected tabIndex is no longer the authored one.
    mobileInput.setAttribute('tabindex', input.getAttribute('tabindex') ?? '0')

    // form is COPIED, not moved: submission needs it next to the name on the source
    // input, and constraint validation needs it here — a hidden input is exempt from
    // validation, so without this the required flatpickr copied over would never be
    // checked when the picker sits outside its form.
    const form = input.getAttribute('form')

    if (form !== null) mobileInput.setAttribute('form', form)

    return () => moved.forEach(([name, value]) => input.setAttribute(name, value))
}

// flatpickr writes the min/max bounds onto the native mobile input as 'Y-m-d' whatever
// the input type is. A datetime-local or time input rejects that and silently drops the
// constraint, so the bounds are rewritten in the format each type actually accepts.
// No-op off mobile, where there is no native input.
function applyNativeBounds(mobileInput, minDate, maxDate) {
    if (!mobileInput) return

    for (const [attribute, date] of [['min', minDate], ['max', maxDate]]) {
        const bound = formatNativeBound(date, mobileInput.type)

        if (bound === null) {
            mobileInput.removeAttribute(attribute)

            continue
        }

        mobileInput.setAttribute(attribute, bound)
    }
}

function formatNativeBound(date, inputType) {
    if (!date) return null

    const pad = (value) => String(value).padStart(2, '0')
    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`

    if (inputType === 'time') return time
    if (inputType === 'datetime-local') return `${day}T${time}`

    return day
}

// Parse a server-side value (entangled form state) into a Date. Handles the
// formats the fields emit: 'Y-m-d', 'Y-m-d\TH:i:s', 'Y-m-d H:i(:s)', months
// like '2026-09' and bare times like '14:30'. Date-only strings get a local-midnight time appended so
// they are not interpreted as UTC.
//
// Everything this returns sits on the granularity the field's wire format can express:
// a whole day for date and range, a whole minute for datetime and time.
//
// Both halves are load-bearing, and they are complements rather than alternatives:
//
//   - Bounds must be GIVEN in that granularity (see the field reference). A bound
//     carrying seconds cannot work: a native input takes whole minutes, so the picker
//     would offer a minute that flatpickr — filtering to the second — then refuses.
//   - Values are FLOORED to it here. Without that, a stored value whose seconds put it
//     just past a wire-format bound is dropped by the same second-precision filter:
//     '2026-06-30T18:00:30' against max '2026-06-30 18:00' renders an empty field over a
//     filled record, and the clear-on-blur then commits ''. Measured against flatpickr
//     4.6.13; leaving values alone was tried and reverted for exactly this.
//
// Flooring costs a `->format('Y-m-d H:i:s')` display showing 10:30:00 for a stored
// 10:30:45 — cosmetic, and only until the first edit, which the wire format truncates
// anyway. Losing the value is not cosmetic. The real fix is one precision contract
// across PHP, wire format and validation; see the known issue in the 0.35.2 notes.
//
// It always rounds DOWN. Rounding a minimum up was tried too, and put a value sitting
// exactly on its own bound outside it — the same empty field, at the other end.
function parseServerValue(value, mode) {
    if (!value || typeof value !== 'string') return null

    if (mode === 'time') {
        const match = value.match(/(\d{1,2}):(\d{2})/)
        if (!match) return null

        const date = new Date()
        date.setHours(Number(match[1]), Number(match[2]), 0, 0)

        return date
    }

    // A month is its first day at local midnight. Built by parts on purpose:
    // `new Date('2026-09')` is a UTC instant that lands on August 31 west of UTC.
    // A fuller stamp ('Y-m-d…') is floored to its month the same way.
    if (mode === 'month') {
        const match = value.trim().match(/^(\d{4})-(\d{1,2})(?:$|[^\d])/)
        if (!match) return null

        const month = Number(match[2])
        if (month < 1 || month > 12) return null

        return new Date(Number(match[1]), month - 1, 1)
    }

    let normalized = value.trim().replace(' ', 'T')
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        normalized += 'T00:00:00'
    }

    const parsed = new Date(normalized)

    if (Number.isNaN(parsed.getTime())) return null

    // A date field stores a day, so its whole domain is local midnight — including a
    // bound written as a full timestamp, which `min="{{ now() }}"` makes an everyday
    // case. Without this the picker offers the boundary day and flatpickr, comparing it
    // against a time of day, refuses it.
    if (mode === 'date' || mode === 'range') {
        parsed.setHours(0, 0, 0, 0)

        return parsed
    }

    parsed.setSeconds(0, 0)

    return parsed
}
